import type { Dict } from "@/types/dict";
import type { CSSProperties, FunctionalComponent, Ref } from "vue";
import type { FormEventMap, FormEventProducer } from "./event";
import type { Get } from "@/utils/object/pick";
import type { FormSchema } from "./schema";
import type { FormFieldOptionsDefinition } from "./options";
import type { IBubbletipBaseProps } from "components/bubbletip/types";
import type { GridBaseProps } from "components/grid/types";
import type { FormEventHooks } from "./eventHook";
import type { IconFactor } from "@/types/elements/icon";

export enum EFormFieldErrorLevel {
  INFO = 'INFO',
  WARNING = 'WARNING',
  ERROR = 'ERROR',
  REQUIRED = 'REQUIRED'
}

export type FormFieldError = {
  level?: EFormFieldErrorLevel;
  disabled?: boolean;
  message: string;
}

export type FormFieldErrorWithPath = FormFieldError & {
  path: string;
}

export type FormFieldValidatorFunc = (attribs: Partial<FormFieldDefinition<any, any, any>>, value: any) => (FormFieldError | undefined | null);

export type FormFieldEventFunc<State extends Dict> = <K extends keyof FormEventMap<State>, Path extends string>(
    path: Path,
    evType: K,
    fun: (ev: Omit<FormEventMap<State>[K], 'field'> & { field: FormFieldReference<State, Get<State, Path> | null | undefined> }) => void
  ) => () => void


export enum EFormFieldDefinitionAction {
  UNSET = 'UNSET',
  ADD_TO_SET = 'ADD_TO_SET',
  REMOVE_FROM_SET = 'REMOVE_FROM_SET'
}

export type FormFieldDefinitionActionBase<T extends EFormFieldDefinitionAction, K extends Dict> = K & {
  type: T;
}

export type FormFieldDefinitionAction = FormFieldDefinitionActionBase<EFormFieldDefinitionAction.ADD_TO_SET, {
  field: string;
}> | FormFieldDefinitionActionBase<EFormFieldDefinitionAction.REMOVE_FROM_SET, {
  field: string;
}> | FormFieldDefinitionActionBase<EFormFieldDefinitionAction.UNSET, {
  field: string;
}>;;


export type FormFieldDefinition<State extends Dict, ValueType = any, ComputedData extends Dict = Dict> = {
  _isFieldDef: true
  type?: string;
  hidden?: boolean;
  transformType?: string;
  transform?: (value: unknown) => ValueType,
  transformInverse?: (value: ValueType) => any,
  onValueChange?: FormFieldDefinitionAction[];
  class?: string;
  wrapperClass?: string;
  classOverride?: string;
  icon?: IconFactor;
  component?: string | FunctionalComponent<{ dataRef: FormFieldInstance<ValueType>, value?: ValueType }>
  label?: string | FunctionalComponent<any>;
  bottomLabel?: string | FunctionalComponent<any>;
  id?: string;
  name?: string;
  subLabel?: string;
  inlineLabel?: boolean;
  labelClass?: string;
  labelPlacement?: string;
  grid?: Partial<GridBaseProps>;
  placeholder?: string;
  tooltip?: string;
  injectedProps?: Dict;
  focused?: boolean;
  touched?: boolean;
  display?: {
    title?: string;
    value?: string;
  },
  value?: ValueType;
  disabled?: boolean;
  locked?: boolean;
  lockedReason?: string;
  lockedReasonBubbletip?: Partial<IBubbletipBaseProps>;
  errorProps?: Partial<IBubbletipBaseProps>;
  options?: FormFieldOptionsDefinition<ValueType | undefined | null>;
  validators?: FormFieldValidatorFunc[];
  required?: boolean;
  showRequired?: boolean;
  errors?: FormFieldError[];
  needs?: string[];
  dependsOn?: any[];
  style?: MaybeRef<CSSProperties>;
  lean?: boolean;
  hooks?: FormEventHooks<State, string, Partial<FormFieldDefinition<State, ValueType, ComputedData>>>;
  $attributes?: Partial<FormFieldDefinition<State>>;
  next?: (state: State, data: ComputedData, previous: FormFieldDefinition<State, ValueType, ComputedData>) => Partial<FormFieldDefinition<State, ValueType, ComputedData>>
}

export type FormFieldGroupDefinition<State extends Dict, Slice extends Dict = Dict, ComputedData extends Dict = Dict> = {
  _isGroupDef: true;
  attributes?: Omit<FormFieldDefinition<State, any, ComputedData>, '_isFieldDef'>;
  fields: FormSchema<Slice, State, void, ComputedData>;
}

export type FormField<State extends Dict, ComputedData extends Dict = Dict> = FormFieldDefinition<State, any, ComputedData> & {
  _isField: true
  path: string
  schemaPath: string;
  name: string;
}

export type FormFieldGroup<State extends Dict, Slice extends Dict = Dict, ComputedData extends Dict = Dict> = FormFieldGroupDefinition<State, Slice, ComputedData> & {
  _isGroup: true;
  path: string;
  schemaPath: string;
  name: string;
}

export type FormFieldReferenceAttributes<State extends Dict, ComputedData extends Dict = Dict> = Omit<
  Partial<FormFieldDefinition<State, any, ComputedData>>,
  'value'
>

export type FormFieldReference<State extends Dict, ValueType = any, ComputedData extends Dict = Dict> = {
  _isFieldRef: true
  __v_skip: true
  isGroup?: boolean;
  schema: FormFieldDefinition<State, any, ComputedData> | FormField<State, ComputedData> | FormFieldGroup<State, any, ComputedData> | FormFieldGroupDefinition<State, any, ComputedData>;
  attributes: Ref<FormFieldReferenceAttributes<State, ComputedData>>;
  value: Ref<ValueType>
  props: any
  children?: FormFieldReference<State, any, ComputedData>[];
  emitter: FormEventProducer<State>;
  path: string;
  name: string;
}

export type FormFieldComponentLookup = Record<string, any>;

export type FormFieldInstance<ValueType = any> = Omit<Partial<FormFieldReferenceAttributes<any, any>>, 'value'> & {
  value: Ref<ValueType>,
  setValue: (val: any, forceChange?: boolean) => void,
  markChanged: () => void;
  setFocused: (focus: boolean) => void,
  setTouched: (touch: boolean) => void
  attributes: Ref<FormFieldReferenceAttributes<any, any>>;
  fieldErrors?: MaybeRef<Array<FormFieldError>>
  __v_skip?: true
}

export const defineField = <State extends Dict, ValueType = any, ComputedData extends Dict = Dict>(
  def: Omit<FormFieldDefinition<State, ValueType, ComputedData>, '_isFieldDef'>
): FormFieldDefinition<State, ValueType, ComputedData> => {
  return {
    ...def,
    _isFieldDef: true
  }
}

export const defineGroup = <State extends Dict, Slice extends Dict, ComputedData extends Dict>(
  def: Omit<FormFieldGroupDefinition<State, Slice, ComputedData>, '_isGroupDef'>
): FormFieldGroupDefinition<State, Slice, ComputedData> => {
  return {
    ...def,
    _isGroupDef: true
  }
}

export const isFormFieldDefinition = <State extends Dict>(
  x: any
): x is FormFieldDefinition<State> => {
  if (typeof x === 'undefined' || x === null) return false
  return typeof x === 'object' && x._isFieldDef === true
}

export const isFormFieldGroupDefinition = <State extends Dict, Slice extends Dict>(
  x: any
): x is FormFieldGroupDefinition<State, Slice> => {
  if (typeof x === 'undefined' || x === null) return false
  return typeof x === 'object' && x._isGroupDef === true
}

export const isFormFieldGroup = <State extends Dict, Slice extends Dict>(
  x: any
): x is FormFieldGroup<State, Slice> => {
  if (typeof x === 'undefined' || x === null) return false
  return typeof x === 'object' && x._isGroup === true
}

export const isFormField = <State extends Dict>(x: any): x is FormField<State> => {
  if (typeof x === 'undefined' || x === null) return false
  return typeof x === 'object' && x._isField === true
}

export const isFormFieldReference = <State extends Dict>(
  x: any
): x is FormFieldReference<State> => {
  if (typeof x === 'undefined' || x === null) return false
  return typeof x === 'object' && x._isFieldRef === true
}

export const getFormFieldErrors = <State extends Dict, ComputedData extends Dict = Dict>(field: FormFieldReference<State, any, ComputedData>, nextValue: any, disabled: boolean = true): FormFieldError[] => {
  return (field.attributes.value.validators || [])
              .map((fun) => fun(field.attributes.value || {}, nextValue))
              .filter((it) => typeof it !== 'undefined' && it !== null)
              .filter((it) => !!it.message && it.message.length > 1).map((it) => ({
                ...it,
                disabled: typeof it?.disabled === 'boolean' ? it.disabled : disabled
              }))
}
